<template>
  <main class="flex justify-center bg-grey w-full min-h-[80vh]">
    <div class="w-full xl:w-5/6 max-xl:px-3 py-5 md:py-10">
      <section>
        <swiper
          :modules="modules"
          :slides-per-view="1"
          :autoplay="{
            delay: 10000,
            disableOnInteraction: true,
            pauseOnMouseEnter: true,
          }"
          :pagination="{ clickable: true }"
          class="mySwiper"
        >
          <swiper-slide v-for="(slide, index) in banners" :key="index">
            <img :src="slide.image" alt="image" class="w-full object-fit" />
          </swiper-slide>
        </swiper>
      </section>

      <div ref="scrollTarget" class="flex gap-5 lg:gap-10 w-full mt-5 md:mt-10">
        <section class="max-md:hidden bg-white md:w-1/3 lg:w-1/4 rounded">
          <div class="bg-white p-3">
            <div class="flex items-center w-full border border-coal text-coal">
              <font-awesome-icon
                icon="fa-solid fa-magnifying-glass"
                class="p-2"
              />
              <input
                type="text"
                :placeholder="
                  category_code === '0403'
                    ? lang === 'mn'
                      ? 'Зөвлөх инженер'
                      : 'Consultant'
                    : lang === 'mn'
                    ? 'Байгууллагын нэр'
                    : 'Organization'
                "
                class="w-full p-2 placeholder:text-sm"
                v-model="searchTerm"
              />
              <button
                @click="clearSearch()"
                class="p-2 text-gray-300 hover:text-coal"
              >
                <font-awesome-icon icon="fa-solid fa-xmark" />
              </button>
              <button
                @click="searchData()"
                class="bg-coal h-full p-3 text-white text-sm hover:bg-black"
              >
                {{ lang === "mn" ? "Хайх" : "Search" }}
              </button>
            </div>
            <p
              v-if="searchTerm && !isValidSearchInput"
              class="text-xs text-gray-500 mt-1"
            >
              {{
                lang === "mn"
                  ? "Хайлтыг кирилл үсгээр хийнэ үү."
                  : "Please use English alphabet for the search."
              }}
            </p>
          </div>

          <ul v-for="main in categories" :key="main">
            <li class="bg-gold text-black font-bold uppercase p-3">
              <router-link :to="`/yp/category/${main.id}`"
                >{{ main.id }}.
                {{ lang === "mn" ? main.nameMn : main.nameEn }}</router-link
              >
            </li>
            <li
              v-for="sub in main.subCategories"
              :key="sub.id"
              class="text-sm w-full"
              :class="
                category_code === sub.category_code
                  ? 'bg-[#d6d6d6] border-l-4 border-gold'
                  : 'text-coal hover:border hover:border-coal'
              "
            >
              <router-link
                :to="`/yp/category/${sub.category_code}`"
                class="block w-full px-3 py-1.5 cursor-pointer"
                >{{ sub.category_code }} - {{ sub.name }}</router-link
              >
            </li>
          </ul>
        </section>

        <section class="w-full md:w-2/3 lg:w-3/4">
          <h1
            v-if="term"
            class="font-bold uppercase text-lg md:text-2xl lg:text-3xl text-coal"
          >
            {{ lang === "mn" ? "Хайлтын утга:" : "Search term:" }}
            {{ term }}
          </h1>
          <h1
            v-else
            class="font-bold uppercase text-lg md:text-2xl lg:text-3xl text-coal"
          >
            {{ category_code }} {{ category_name }}
          </h1>

          <h6 class="text-gray-500 text-sm uppercase">
            {{ lang === "mn" ? "НИЙТ ИЛЭРЦ:" : "TOTAL RESULTS:" }}
            {{ totalCount }}
          </h6>

          <div
            v-if="companies.length > 0 && !isLoading"
            class="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-8 overflow-hidden mt-5"
          >
            <div v-for="company in companies" :key="company.id">
              <router-link :to="`/yp/company/${company.id}`">
                <div
                  class="flex justify-center items-center overflow-hidden aspect-[1/1] hover:scale-105"
                >
                  <img
                    v-if="company?.logo"
                    :src="company?.logo"
                    alt="logo"
                    class="w-full h-full object-contain"
                  />
                  <p v-else class="text-gray-300 text-5xl font-bold">ЛОГО</p>
                </div>
                <h6 class="font-bold text-sm mt-1.5">{{ company.name }}</h6>
                <div class="grid grid-cols-3 md:grid-cols-4 gap-1 mt-1.5">
                  <router-link
                    v-for="(category, index) in company.all_categories"
                    :key="index"
                    :to="`/yp/category/${category}`"
                    class="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded-full hover:bg-gray-300"
                  >
                    {{ category }}
                  </router-link>
                </div>
              </router-link>
            </div>
          </div>

          <div
            v-else-if="consultants.length > 0 && !isLoading"
            class="mt-5 overflow-x-scroll"
          >
            <table class="w-full border rounded-lg divide-y divide-gray-200">
              <thead class="bg-gray-300 text-black">
                <tr>
                  <th class="px-4 py-2 text-center">
                    {{ lang === "mn" ? "Нэр" : "Full Name" }}
                  </th>
                  <th class="px-4 py-2 text-center">
                    {{ lang === "mn" ? "Мэргэжил" : "Profession" }}
                  </th>
                  <th class="px-4 py-2 text-center">
                    {{ lang === "mn" ? "Зөвлөгөө" : "Advice Range" }}
                  </th>
                  <th class="px-4 py-2 text-center">
                    {{ lang === "mn" ? "Утас" : "Phone Number" }}
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="person in consultants" :key="person.id">
                  <td class="px-4 py-2 text-sm text-nowrap">
                    {{
                      lang === "mn"
                        ? person.person_info?.translations?.name?.mn === "NULL"
                          ? ""
                          : person.person_info?.translations?.name?.mn
                        : person.person_info?.translations?.name?.en === "NULL"
                        ? ""
                        : person.person_info?.translations?.name?.en
                    }}
                  </td>
                  <td class="px-4 py-2 text-sm">
                    {{
                      lang === "mn"
                        ? person.person_info?.translations?.profession?.mn ===
                          "NULL"
                          ? ""
                          : person.person_info?.translations?.profession?.mn
                        : person.person_info?.translations?.profession?.en ===
                          "NULL"
                        ? ""
                        : person.person_info?.translations?.profession?.en
                    }}
                  </td>
                  <td class="px-4 py-2 text-sm">
                    {{
                      lang === "mn"
                        ? person.person_info?.translations?.advice_range?.mn ===
                          "NULL"
                          ? ""
                          : person.person_info?.translations?.advice_range?.mn
                        : person.person_info?.translations?.advice_range?.en ===
                          "NULL"
                        ? ""
                        : person.person_info?.translations?.advice_range?.en
                    }}
                  </td>
                  <td class="px-4 py-2 text-sm text-center">
                    {{ person.phone_number ? person.phone_number : "" }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            v-else-if="
              companies.length === 0 && consultants.length === 0 && !isLoading
            "
            class="flex flex-col justify-center items-center h-[60vh] w-full text-gray-500"
          >
            <img
              src="../../../assets/svg/no-data.svg"
              alt="not-found"
              class="w-1/3"
            />
            <p class="mt-4 text-lg font-medium">
              {{
                lang === "mn"
                  ? "Тохирох хайлтын үр дүн олдсонгүй."
                  : "No results found."
              }}
            </p>
          </div>

          <div
            v-else-if="isLoading"
            class="flex justify-center items-center h-[60vh]"
          >
            <YPLoading />
          </div>

          <div
            v-if="
              (companies.length > 0 || consultants.length > 0) && !isLoading
            "
            class="flex justify-center mt-10"
          >
            <YPPagination
              :totalPages="totalPages"
              :modelValue="currentPage"
              @page-changed="onPageChanged"
            />
          </div>
        </section>
      </div>
    </div>
  </main>
</template>

<script>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { Swiper, SwiperSlide } from "swiper/vue";
import {
  Navigation,
  Pagination,
  Scrollbar,
  A11y,
  Autoplay,
} from "swiper/modules";
import "swiper/css/bundle";
import YPPagination from "@/components/YellowPage/YPPagination.vue";
import YPLoading from "@/components/YellowPage/YPLoading.vue";

export default {
  name: "YPCategory",
  components: {
    Swiper,
    SwiperSlide,
    YPPagination,
    YPLoading,
  },
  setup() {
    const route = useRoute();
    const store = useStore();

    const category_code = ref(route.params.id);
    const category_name = ref(null);
    const lang = computed(() => store.getters.getLanguage);

    const isLoading = ref(false);
    const banners = ref([]);
    const companies = ref([]);
    const consultants = ref([]);
    const categories = ref([]);
    const searchTerm = ref(null);
    const term = ref(null);
    const scrollTarget = ref(null);

    const dataPerPage = ref(category_code.value === "0403" ? 50 : 36);
    const totalCount = ref(null);
    const totalPages = ref(1);
    const currentPage = ref(1);

    const fetchDataByCategory = async () => {
      isLoading.value = true;
      try {
        if (category_code.value === "0403") {
          const response = await store.dispatch(
            "yellow/fetchConsultantsByCategory",
            {
              lang: lang.value,
              page: currentPage.value,
              limit: dataPerPage.value,
            }
          );
          category_name.value = response?.data?.category_name || null;
          companies.value = [];
          term.value = null;
          consultants.value = response?.data?.consultants || [];
          totalPages.value = response?.data?.totalPages || 1;
          totalCount.value = response?.data?.total || 0;
        } else {
          const response = await store.dispatch(
            "yellow/fetchCompaniesByCategory",
            {
              lang: lang.value,
              category_code: category_code.value,
              page: currentPage.value,
              limit: dataPerPage.value,
            }
          );
          category_name.value = response?.data?.category_name || null;
          consultants.value = [];
          term.value = null;
          companies.value = response?.data?.companies || [];
          totalPages.value = response?.data?.totalPages || 1;
          totalCount.value = response?.data?.total || 0;
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        isLoading.value = false;
      }
    };

    // const checkData = () => {
    //   if (category_code.value === "0403") {
    //     const existing = store.getters["yellow/getConsultantsByCategory"];
    //     if (!existing || existing.length === 0) {
    //       fetchDataByCategory();
    //     } else {
    //       category_name.value = existing.category_name || null;
    //       companies.value = [];
    //       term.value = null;
    //       consultants.value = existing.consultants;
    //       totalPages.value = existing.totalPages || 1;
    //       totalCount.value = existing.total || 0;
    //     }
    //   } else {
    //     const existing = store.getters["yellow/getCompaniesByCategory"];

    //     if (!existing || existing.length === 0) {
    //       fetchDataByCategory();
    //     } else {
    //       category_name.value = existing.category_name || null;
    //       consultants.value = [];
    //       term.value = null;
    //       companies.value = existing.companies;
    //       totalPages.value = existing.totalPages || 1;
    //       totalCount.value = existing.total || 0;
    //     }
    //   }
    // };

    const fetchAllCategories = async () => {
      try {
        const response = await store.dispatch(
          "yellow/fetchAllCategories",
          lang.value
        );
        if (response?.data?.categories) {
          categories.value = response.data.categories;
        } else {
          console.warn("No categories in response", response);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };

    const checkCategories = () => {
      const existingCategories = store.getters["yellow/getAllCategories"];
      if (!existingCategories || existingCategories.length === 0) {
        fetchAllCategories();
      } else {
        categories.value = existingCategories;
      }
    };

    const fetchBanners = async () => {
      try {
        const response = await store.dispatch("yellow/fetchBanners");
        if (response?.data?.banners) {
          banners.value = response.data.banners;
        } else {
          console.warn("No categories in response", response);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };

    const checkBanners = () => {
      const existingBanners = store.getters["yellow/getBanners"];
      if (!existingBanners || existingBanners.length === 0) {
        fetchBanners();
      } else {
        banners.value = existingBanners;
      }
    };

    onMounted(() => {
      checkCategories();
      checkBanners();
      fetchDataByCategory();
    });

    watch(
      () => route.params.id,
      (newId) => {
        category_code.value = newId;
        currentPage.value = 1;
        fetchDataByCategory();
      }
    );

    watch(lang, async (newLang, oldLang) => {
      if (newLang !== oldLang) {
        await fetchAllCategories();
        await fetchDataByCategory();
      }
    });

    const onPageChanged = async (page) => {
      currentPage.value = page;
      await fetchDataByCategory();

      const offset = 100;

      const element = scrollTarget.value;
      if (element) {
        const topPosition =
          element.getBoundingClientRect().top + window.scrollY - offset;

        window.scrollTo({
          top: topPosition,
          behavior: "smooth",
        });
      }
    };

    const searchData = async () => {
      isLoading.value = true;
      term.value = searchTerm.value;

      try {
        if (searchTerm.value) {
          if (category_code.value === "0403") {
            const response = await store.dispatch("yellow/searchConsultants", {
              lang: lang.value,
              page: currentPage.value,
              limit: dataPerPage.value,
              term: searchTerm.value.toUpperCase(),
            });
            companies.value = [];
            consultants.value = response?.data?.consultants || [];
            totalPages.value = response?.data?.totalPages || 1;
            totalCount.value = response?.data?.total || 0;
          } else {
            const response = await store.dispatch("yellow/searchCompanies", {
              lang: lang.value,
              page: currentPage.value,
              limit: dataPerPage.value,
              term: searchTerm.value.toUpperCase(),
            });
            consultants.value = [];
            companies.value = response?.data?.companies || [];
            totalPages.value = response?.data?.totalPages || 1;
            totalCount.value = response?.data?.total || 0;
          }
        } else {
          await fetchDataByCategory();
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        isLoading.value = false;
      }
    };

    const clearSearch = () => {
      if (searchTerm.value) {
        searchTerm.value = null;
        term.value = null;
        fetchDataByCategory();
      }
    };

    const isValidSearchInput = computed(() => {
      if (!searchTerm.value) return true;
      if (lang.value === "mn") {
        return /^[\u0400-\u04FF\s]+$/.test(searchTerm.value);
      } else {
        return /^[A-Za-z\s]+$/.test(searchTerm.value);
      }
    });

    return {
      lang,
      category_code,
      category_name,
      isLoading,
      banners,
      companies,
      consultants,
      categories,
      dataPerPage,
      totalCount,
      totalPages,
      currentPage,
      searchTerm,
      onPageChanged,
      fetchDataByCategory,
      searchData,
      term,
      clearSearch,
      isValidSearchInput,
      scrollTarget,
      modules: [Navigation, Pagination, Scrollbar, A11y, Autoplay],
    };
  },
};
</script>

<style scoped>
.mySwiper {
  --swiper-pagination-color: #333333;
  --swiper-pagination-bullet-size: 16px;
  --swiper-pagination-bullet-inactive-color: #fff;
  --swiper-pagination-bullet-inactive-opacity: 0.4;
  --swiper-pagination-bullet-opacity: 1;
  --swiper-pagination-bullet-horizontal-gap: 6px;
  --swiper-pagination-bullet-vertical-gap: 6px;
  --swiper-button-prev: #333333;
}
</style>
